import React from "react";
import { Modal, Form, Button, Nav, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/Timesheet.css";
import weekFromStart, { mergeDaysWithWeek } from "../utils/dateMaker";
import Axios from "../utils/Axios";

import {
  EMP_WEEK_RECORD,
  GET_EACH_EMP_TIMESHEET,
  LIST_ALL_TIMESHEETS,
  SUBMIT_TIMESHEET,
  TIMESHEET_RECORD,
} from "../config/API_Collection";
import AuthHandler from "../utils/AuthHandler";
import Toast from "../utils/Toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../utils/navigation";

export function AddTimeEmployee({ setShowAddTime, weekProp, emp_id = false }) {
  // State to track expanded/collapsed job groups
  const [expandedGroups, setExpandedGroups] = useState({});

  // Toggle expand/collapse for a group
  const toggleGroup = (projectNumber) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [projectNumber]: !prev[projectNumber],
    }));
  };
  const [activeDay, setActiveDay] = useState("Friday");
  const [weekData, setWeekData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filterJobNumber, setFilterJobNumber] = useState(""); // Filter state

  // submit button should disable immediately on click (no spinner UI)
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const submittingRef = useRef(false);
  const firstFocusRef = useRef(false); // Track if first input has been focused

  // Toast with a key so it re-renders even with same message
  const [toast, setToast] = useState({ message: "", type: "", key: 0 });
  const showToast = (message, type) =>
    setToast({ message, type, key: Date.now() });

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : null) ||
      error?.message ||
      fallback
    );
  };

  // task_assignment_id -> iso -> { hours, comment }
  const [entries, setEntries] = useState({});
  // per-entry indicators
  const [saved, setSaved] = useState({}); // green ✔
  const [dirty, setDirty] = useState({}); // yellow !

  // NEW: per-entry errors (e.g., required comment)
  // shape: errors[taskAssignmentId][iso] = ""
  const [errors, setErrors] = useState({});

  // Store timesheet_id - may be returned when first entry is saved
  const [timesheetId, setTimesheetId] = useState(null);

  const fetchEmployeeDetails = async () => {
    if (emp_id) {
      try {
        setIsLoading(true);
        const response = await Axios.get(
          `${GET_EACH_EMP_TIMESHEET}week_start=${weekProp?.week_start}&emp_id=${emp_id}`,
        );
        if (response.status === 200) {
          setWeekData(response.data?.data?.[0] || {});
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        setIsLoading(true);
        const response = await Axios.get(
          `${EMP_WEEK_RECORD}${weekProp?.week_start}`,
        );
        if (response.status === 200) {
          setWeekData(response.data?.data?.[0] || {});
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (weekProp?.week_start) fetchEmployeeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekProp?.week_start]);

  const daysBase = useMemo(
    () => [
      { d: 0, key: "Monday", label: "Monday", date: "19th Aug" },
      { d: 1, key: "Tuesday", label: "Tuesday", date: "20th Aug" },
      { d: 2, key: "Wednesday", label: "Wednesday", date: "21st Aug" },
      { d: 3, key: "Thursday", label: "Thursday", date: "22nd Aug" },
      { d: 4, key: "Friday", label: "Friday", date: "23rd Aug" },
      { d: 5, key: "Saturday", label: "Saturday", date: "24th Aug" },
      { d: 6, key: "Sunday", label: "Sunday", date: "25th Aug" },
    ],
    [],
  );

  const days = useMemo(
    () => mergeDaysWithWeek(daysBase, weekProp?.week_start || ""),
    [daysBase, weekProp?.week_start],
  );

  // Auto-focus today if within this week
  const todayIso = useMemo(() => new Date().toLocaleDateString("en-CA"), []);
  useEffect(() => {
    if (Array.isArray(days) && days.length === 7) {
      const match = days.find((d) => d.iso === todayIso);
      if (match) setActiveDay(match.key);
    }
  }, [days, todayIso]);

  // Auto-focus first hours input on initial mount (only once)
  useEffect(() => {
    if (!isLoading && !firstFocusRef.current && !filterJobNumber && weekData?.tasks?.length > 0) {
      setTimeout(() => {
        const firstInput = document.querySelector(".timesheet-body .time-input");
        if (firstInput) {
          firstInput.focus();
          firstFocusRef.current = true;
        }
      }, 0);
    }
  }, [isLoading, weekData?.tasks?.length]);

  const activeIso = useMemo(
    () => days.find((d) => d.key === activeDay)?.iso,
    [days, activeDay],
  );

  // Initialize entries
  useEffect(() => {
    if (!isLoading && Array.isArray(weekData?.tasks) && days.length === 7) {
      const init = {};
      weekData.tasks.forEach((t) => {
        const byIso = {};
        days.forEach((d) => {
          byIso[d.iso] = { hours: 0, comment: "" };
        });
        (t.days || []).forEach((x) => {
          if (x?.date && byIso[x.date] !== undefined) {
            byIso[x.date] = {
              hours:
                typeof x.hours === "number" ? x.hours : Number(x.hours) || 0,
              comment: x.comment || "",
            };
          }
        });
        init[t.task_assignment_id] = byIso;
      });
      setEntries(init);
      setSaved({});
      setDirty({});
      setErrors({}); // reset errors on new load
      // Update timesheetId from weekData if available
      const id = weekData?.timesheet_id ?? weekData?.id;
      if (id) setTimesheetId(id);
    }
  }, [isLoading, weekData?.tasks, days]);

  // helpers to set/clear error for a specific cell
  const setCellError = (taskAssignmentId, iso, msg) => {
    setErrors((prev) => ({
      ...prev,
      [taskAssignmentId]: { ...(prev[taskAssignmentId] || {}), [iso]: msg },
    }));
  };
  const clearCellError = (taskAssignmentId, iso) => {
    setErrors((prev) => {
      const t = { ...(prev[taskAssignmentId] || {}) };
      delete t[iso];
      return { ...prev, [taskAssignmentId]: t };
    });
  };

  // Change handlers mark dirty and clear saved + validate comment
  const handleChange = (taskAssignmentId, iso, field, value) => {
    setEntries((prev) => {
      const currentTask = prev[taskAssignmentId] || {};
      const currentDay = currentTask[iso] || { hours: 0, comment: "" };
      const nextVal =
        field === "hours" ? (value === "" ? "" : Number(value)) : value;

      // update base entries
      const next = {
        ...prev,
        [taskAssignmentId]: {
          ...currentTask,
          [iso]: {
            ...currentDay,
            [field]: nextVal,
          },
        },
      };

      return next;
    });

    // mark dirty for this cell
    setDirty((prev) => ({
      ...prev,
      [taskAssignmentId]: {
        ...(prev[taskAssignmentId] || {}),
        [iso]: true,
      },
    }));

    // clear saved flag if it was saved before
    setSaved((prev) => {
      const taskMap = { ...(prev[taskAssignmentId] || {}) };
      if (taskMap[iso]) taskMap[iso] = false;
      return { ...prev, [taskAssignmentId]: taskMap };
    });

    // required validation for comment
    // No required validation for comment
    clearCellError(taskAssignmentId, iso);
  };

  // Helper function to get holiday/leave info from week_day_list
  const getDayInfo = (iso) => {
    if (!Array.isArray(weekData?.week_day_list)) return null;
    for (let dayObj of weekData.week_day_list) {
      if (dayObj[iso]) {
        return dayObj[iso];
      }
    }
    return null;
  };

  const dayTotals = useMemo(() => {
    return days.map((d) => {
      let sum = 0;
      Object.values(entries).forEach((byIso) => {
        const v = byIso?.[d.iso]?.hours;
        const n = typeof v === "string" ? parseFloat(v) : Number(v);
        if (!isNaN(n)) sum += n;
      });
      return sum;
    });
  }, [entries, days]);

  const weekTotal = useMemo(() => {
    return days.reduce((total, day, idx) => {
      const dayInfo = getDayInfo(day.iso);
      const dayTotal =
        dayInfo?.isHoliday || dayInfo?.isLeave ? 8 : dayTotals[idx];
      return total + dayTotal;
    }, 0);
  }, [days, dayTotals, weekData?.week_day_list]);

  // Filter tasks by job number
  const filteredTasks = useMemo(() => {
    if (!Array.isArray(weekData?.tasks)) return [];
    if (!filterJobNumber.trim()) return weekData.tasks;
    const searchTerm = filterJobNumber.toLowerCase().trim();
    return weekData.tasks.filter((task) => {
      const jobNumber = (task?.project_number || "").toLowerCase();
      const taskName = (task?.task_name || "").toLowerCase();
      return jobNumber.includes(searchTerm) || taskName.includes(searchTerm);
    });
  }, [weekData?.tasks, filterJobNumber]);

  // Group filtered tasks by project number
  const groupedTasks = useMemo(() => {
    const groups = {};
    filteredTasks.forEach((task) => {
      const key = task.project_number || "No Project";
      if (!groups[key]) {
        groups[key] = {
          project_number: task.project_number,
          project_name: task.project_name,
          tasks: [],
        };
      }
      groups[key].tasks.push(task);
    });
    // Sort groups by project_number in descending order
    return Object.values(groups).sort((a, b) => {
      const aNum = a.project_number ? parseInt(a.project_number.replace(/\D/g, ""), 10) : 0;
      const bNum = b.project_number ? parseInt(b.project_number.replace(/\D/g, ""), 10) : 0;
      return bNum - aNum; // Descending order
    });
  }, [filteredTasks]);

  // Ensure all groups are expanded by default when groupedTasks changes
  useEffect(() => {
    if (groupedTasks && groupedTasks.length > 0) {
      const allExpanded = {};
      groupedTasks.forEach((g) => {
        allExpanded[g.project_number] = true;
      });
      setExpandedGroups(allExpanded);
    }
  }, [groupedTasks]);

  // Submitted check
  const coerceTrue = (v) => {
    if (v === true) return true;
    if (v === 1 || v === "1") return true;
    const s = String(v || "").toLowerCase();
    return s === "true" || s === "submitted";
  };
  const isAlreadySubmitted = useMemo(() => {
    const wd = weekData || {};
    return (
      coerceTrue(wd.is_submitted) ||
      coerceTrue(wd.submitted) ||
      coerceTrue(wd.isSubmitted) ||
      coerceTrue(wd.status) ||
      coerceTrue(wd.timesheet_status) ||
      coerceTrue(weekProp?.is_submitted) ||
      coerceTrue(weekProp?.status)
    );
  }, [weekData, weekProp]);

  // Check if status code is 2 (approved)
  const isApproved = useMemo(() => {
    const status = weekData?.status ?? weekProp?.status;
    return status === 2 || status === "2";
  }, [weekData, weekProp]);

  // Submit (NO spinner UI; disable button immediately; always show toast)
  const handleSubmit = async () => {
    if (isAlreadySubmitted || submitDisabled) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    // disable the button immediately on click
    setSubmitDisabled(true);

    try {
      const response = await Axios.post(SUBMIT_TIMESHEET, {
        timesheet_id:
          timesheetId ??
          weekProp?.timesheet_id ??
          weekData?.timesheet_id ??
          weekData?.id ??
          weekProp?.id,
      });

      if (response?.status === 200 || response?.status === 201) {
        showToast("Timesheet submitted successfully", "success");
      } else {
        showToast("Failed to submit timesheet", "failed");
      }
    } catch (error) {
      AuthHandler(error?.response?.status, setToast, navigate);
      const msg = getApiErrorMessage(error, "Failed to submit timesheet");
      showToast(msg, "failed");
    } finally {
      submittingRef.current = false;
      // keep the button disabled after the first click as requested
    }
  };

  // Save (Enter on hours OR comment)
  const saveEntry = async (taskAssignmentId, iso) => {
    const hours =
      entries?.[taskAssignmentId]?.[iso]?.hours === ""
        ? 0
        : Number(entries?.[taskAssignmentId]?.[iso]?.hours) || 0;
    const noteRaw = entries?.[taskAssignmentId]?.[iso]?.comment ?? "";
    const note = noteRaw.trim();

    // REQUIRED: block save if comment empty
    // No required comment for save
    clearCellError(taskAssignmentId, iso);

    const payload = {
      task_assignment_id: taskAssignmentId,
      hours,
      log_date: iso,
      note,
      week_number: weekProp?.week_no,
    };

    try {
      const response = await Axios.post(TIMESHEET_RECORD, payload);
      if (response?.status === 200 || response?.status === 201) {
        showToast("Timesheet record saved successfully", "success");

        // Capture timesheet_id from response if returned (for first-time submissions)
        const returnedTimesheetId =
          response?.data?.data?.timesheet_id ?? response?.data?.timesheet_id;
        if (returnedTimesheetId && !timesheetId) {
          setTimesheetId(returnedTimesheetId);
        }

        setSaved((prev) => ({
          ...prev,
          [taskAssignmentId]: {
            ...(prev[taskAssignmentId] || {}),
            [iso]: true,
          },
        }));
        setDirty((prev) => ({
          ...prev,
          [taskAssignmentId]: {
            ...(prev[taskAssignmentId] || {}),
            [iso]: false,
          },
        }));
      } else {
        showToast(
          response?.data?.message || "Timesheet record save failed",
          "failed",
        );
      }
    } catch (error) {
      AuthHandler(error?.response?.status, setToast, navigate);
      const msg = getApiErrorMessage(
        error,
        "Timesheet record save failed",
      );
      showToast(msg, "failed");
    }
  };

  const handleCommentKeyDown = (e, taskAssignmentId, iso) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      saveEntry(taskAssignmentId, iso);
      e.currentTarget.blur();
    }
  };

  const handleHoursKeyDown = (e, taskAssignmentId, iso) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      saveEntry(taskAssignmentId, iso);
    }
  };

  return (
    <Modal show size="xl" centered backdrop="static" className="">
      {/* Scoped CSS to remove number spinners in all browsers */}
      <style>{`
        .no-spinners::-webkit-outer-spin-button,
        .no-spinners::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .no-spinners { -moz-appearance: textfield; }
        .field-error {
          font-size: 0.85rem;
          color: #dc3545; /* Bootstrap danger */
          margin-top: 0.25rem;
        }
        .field-warning {
          font-size: 0.85rem;
          color: #ffc107; /* Bootstrap warning */
          margin-top: 0.25rem;
        }
      `}</style>

      {/* Header */}
      <Modal.Header className="border-0 px-4 pt-4 pb-2 justify-content-between align-items-center timesheet-body timesheet-y">
        <h4 className="timesheet-title">Time Sheet</h4>
        <h6 className="timesheet-date">{weekProp?.week_date}</h6>
      </Modal.Header>
      <div className="divider title-margin" />

      {/* Body */}
      <Modal.Body className="px-4 timesheet-body">
        <div className="position-relative">
          {isLoading && (
            <div className="loading-overlay">
              <Spinner animation="border" role="status" />
              <span className="ms-2">Loading…</span>
            </div>
          )}
          <Toast key={toast.key} message={toast.message} type={toast.type} />
          <div className={isLoading ? "opacity-50 pe-none" : ""}>
            <div className="d-flex justify-content-between align-items-end mb-4">
              <div className="d-flex align-items-center gap-3">
                <div className="fw-semibold" style={{ whiteSpace: "nowrap" }}>
                  Allocated Tasks
                </div>
                <Form.Control
                  type="text"
                  placeholder="Filter by Job number"
                  className="filter-input"
                  style={{ width: "250px", height: "38px" }}
                  value={filterJobNumber}
                  onChange={(e) => setFilterJobNumber(e.target.value)}
                />
              </div>

              <Nav
                variant="tabs"
                activeKey={activeDay}
                className="timesheet-tabs"
              >
                {days.map((day) => {
                  // Find holiday/leave info for this day
                  const dayInfo = getDayInfo(day.iso);
                  let specialText = null;
                  if (dayInfo?.isHoliday) specialText = "Public Holiday";
                  else if (dayInfo?.isLeave) specialText = "Paid Leave";
                  return (
                    <Nav.Item key={day.key}>
                      <Nav.Link
                        eventKey={day.key}
                        onClick={() => setActiveDay(day.key)}
                      >
                        <div className="day-tab">
                          <div
                            style={{
                              color:
                                dayInfo?.isHoliday || dayInfo?.isLeave
                                  ? "#bfc2c7"
                                  : undefined,
                            }}
                          >
                            {day.label}
                          </div>
                          <small
                            style={{
                              color:
                                dayInfo?.isHoliday || dayInfo?.isLeave
                                  ? "#bfc2c7"
                                  : undefined,
                            }}
                          >
                            {day.date}
                          </small>
                          {specialText && (
                            <div
                              style={{
                                color: "#ffc107",
                                fontSize: "0.8em",
                                marginTop: 2,
                              }}
                            >
                              {specialText}
                            </div>
                          )}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  );
                })}
              </Nav>
            </div>

            {/* Card with tasks - scrollable container */}
            <div
              className="card border rounded-3 d-flex flex-column"
              style={{ maxHeight: "400px" }}
            >
              <div
                className="task-list-scrollable p-3"
                style={{ overflowY: "auto", flex: "1 1 auto" }}
              >
                <table className="table table-borderless align-middle mb-0">
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="3">
                          <div className="placeholder-glow">
                            <span className="placeholder col-7" />
                            <span className="placeholder col-4" />
                            <span className="placeholder col-5" />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        // Check if the active day is a public holiday or leave
                        const dayInfo = getDayInfo(activeIso);
                        if (dayInfo?.isHoliday) {
                          return (
                            <>
                              <tr>
                                <td
                                  style={{
                                    width: "40%",
                                    paddingLeft: "2rem",
                                    fontWeight: "bold",
                                    color: "#222",
                                  }}
                                >
                                  Public Holiday
                                </td>
                                <td style={{ width: "20%" }}>
                                  <Form.Control
                                    type="number"
                                    value={8}
                                    readOnly
                                    style={{
                                      background: "#e9ecef",
                                      color: "#222",
                                      fontWeight: "bold",
                                      fontSize: "1.05em",
                                    }}
                                    className="time-input no-spinners"
                                  />
                                </td>
                                <td style={{ width: "40%" }}>
                                  <Form.Control
                                    type="text"
                                    value={dayInfo.comment || "-"}
                                    readOnly
                                    style={{
                                      background: "#e9ecef",
                                      color: "#222",
                                      fontWeight: "bold",
                                      fontSize: "1.05em",
                                    }}
                                    className="comment-input"
                                  />
                                </td>
                              </tr>
                              {/* Render all other tasks grouped by project, with expand/collapse, all fields read-only and gray */}
                              {groupedTasks.map((group, groupIdx) => (
                                <React.Fragment key={group.project_number}>
                                  {/* Project Header Row (bold, no background, with expand/collapse icon) */}
                                  <tr>
                                    <td colSpan="3" className="py-2">
                                      <span
                                        style={{
                                          fontWeight: "bold",
                                          cursor: "pointer",
                                          userSelect: "none",
                                        }}
                                        onClick={() =>
                                          toggleGroup(group.project_number)
                                        }
                                      >
                                        {expandedGroups[
                                          group.project_number
                                        ] ? (
                                          <span style={{ marginRight: 8 }}>
                                            ▼
                                          </span>
                                        ) : (
                                          <span style={{ marginRight: 8 }}>
                                            ▶
                                          </span>
                                        )}
                                        {group.project_number} -{" "}
                                        {group.project_name}
                                      </span>
                                    </td>
                                  </tr>
                                  {/* Task Rows (read-only, gray fields, only if expanded) */}
                                  {expandedGroups[group.project_number] &&
                                    group.tasks.map((task, idx) => (
                                      <tr key={task.task_assignment_id}>
                                        <td
                                          style={{
                                            width: "40%",
                                            paddingLeft: "2rem",
                                          }}
                                        >
                                          {task?.task_name}
                                        </td>
                                        <td style={{ width: "20%" }}>
                                          <Form.Control
                                            type="number"
                                            value={
                                              entries?.[
                                                task.task_assignment_id
                                              ]?.[activeIso]?.hours || ""
                                            }
                                            readOnly
                                            tabIndex={-1}
                                            style={{
                                              background: "#e9ecef",
                                              color: "#222",
                                              pointerEvents: "none",
                                              boxShadow: "none",
                                              borderColor: "#dee2e6",
                                            }}
                                            className="time-input no-spinners"
                                          />
                                        </td>
                                        <td style={{ width: "40%" }}>
                                          <Form.Control
                                            type="text"
                                            value={
                                              entries?.[
                                                task.task_assignment_id
                                              ]?.[activeIso]?.comment || ""
                                            }
                                            readOnly
                                            tabIndex={-1}
                                            style={{
                                              background: "#e9ecef",
                                              color: "#222",
                                              pointerEvents: "none",
                                              boxShadow: "none",
                                              borderColor: "#dee2e6",
                                            }}
                                            className="comment-input"
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  {/* Divider between groups */}
                                  {groupIdx < groupedTasks.length - 1 && (
                                    <tr>
                                      <td
                                        colSpan="3"
                                        style={{
                                          borderBottom: "2px solid #e0e0e0",
                                          padding: 0,
                                        }}
                                      />
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </>
                          );
                        }
                        if (dayInfo?.isLeave) {
                          return (
                            <>
                              <tr>
                                <td
                                  style={{
                                    width: "40%",
                                    paddingLeft: "2rem",
                                    fontWeight: "bold",
                                    color: "#222",
                                  }}
                                >
                                  Paid Leave
                                </td>
                                <td style={{ width: "20%" }}>
                                  <Form.Control
                                    type="number"
                                    value={8}
                                    readOnly
                                    style={{
                                      background: "#e9ecef",
                                      color: "#222",
                                      fontWeight: "bold",
                                      fontSize: "1.05em",
                                    }}
                                    className="time-input no-spinners"
                                  />
                                </td>
                                <td style={{ width: "40%" }}>
                                  <Form.Control
                                    type="text"
                                    value={dayInfo.comment || "-"}
                                    readOnly
                                    style={{
                                      background: "#e9ecef",
                                      color: "#222",
                                      fontWeight: "bold",
                                      fontSize: "1.05em",
                                    }}
                                    className="comment-input"
                                  />
                                </td>
                              </tr>
                              {/* Render all other tasks grouped by project, with expand/collapse, all fields read-only and gray */}
                              {groupedTasks.map((group, groupIdx) => (
                                <React.Fragment key={group.project_number}>
                                  {/* Project Header Row (bold, no background, with expand/collapse icon) */}
                                  <tr>
                                    <td colSpan="3" className="py-2">
                                      <span
                                        style={{
                                          fontWeight: "bold",
                                          cursor: "pointer",
                                          userSelect: "none",
                                        }}
                                        onClick={() =>
                                          toggleGroup(group.project_number)
                                        }
                                      >
                                        {expandedGroups[
                                          group.project_number
                                        ] ? (
                                          <span style={{ marginRight: 8 }}>
                                            ▼
                                          </span>
                                        ) : (
                                          <span style={{ marginRight: 8 }}>
                                            ▶
                                          </span>
                                        )}
                                        {group.project_number} -{" "}
                                        {group.project_name}
                                      </span>
                                    </td>
                                  </tr>
                                  {/* Task Rows (read-only, gray fields, only if expanded) */}
                                  {expandedGroups[group.project_number] &&
                                    group.tasks.map((task, idx) => (
                                      <tr key={task.task_assignment_id}>
                                        <td
                                          style={{
                                            width: "40%",
                                            paddingLeft: "2rem",
                                          }}
                                        >
                                          {task?.task_name}
                                        </td>
                                        <td style={{ width: "20%" }}>
                                          <Form.Control
                                            type="number"
                                            value={
                                              entries?.[
                                                task.task_assignment_id
                                              ]?.[activeIso]?.hours || ""
                                            }
                                            readOnly
                                            tabIndex={-1}
                                            style={{
                                              background: "#e9ecef",
                                              color: "#222",
                                              pointerEvents: "none",
                                              boxShadow: "none",
                                              borderColor: "#dee2e6",
                                            }}
                                            className="time-input no-spinners"
                                          />
                                        </td>
                                        <td style={{ width: "40%" }}>
                                          <Form.Control
                                            type="text"
                                            value={
                                              entries?.[
                                                task.task_assignment_id
                                              ]?.[activeIso]?.comment || ""
                                            }
                                            readOnly
                                            tabIndex={-1}
                                            style={{
                                              background: "#e9ecef",
                                              color: "#222",
                                              pointerEvents: "none",
                                              boxShadow: "none",
                                              borderColor: "#dee2e6",
                                            }}
                                            className="comment-input"
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  {/* Divider between groups */}
                                  {groupIdx < groupedTasks.length - 1 && (
                                    <tr>
                                      <td
                                        colSpan="3"
                                        style={{
                                          borderBottom: "2px solid #e0e0e0",
                                          padding: 0,
                                        }}
                                      />
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </>
                          );
                        }
                        // Default: normal rendering
                        return groupedTasks.length > 0 ? (
                          groupedTasks.map((group, groupIdx) => (
                            <React.Fragment key={group.project_number}>
                              {/* Project Header Row (bold, no background, with expand/collapse icon) */}
                              <tr>
                                <td colSpan="3" className="py-2">
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      cursor: "pointer",
                                      userSelect: "none",
                                    }}
                                    onClick={() =>
                                      toggleGroup(group.project_number)
                                    }
                                  >
                                    {expandedGroups[group.project_number] ? (
                                      <span style={{ marginRight: 8 }}>▼</span>
                                    ) : (
                                      <span style={{ marginRight: 8 }}>▶</span>
                                    )}
                                    {group.project_number} -{" "}
                                    {group.project_name}
                                  </span>
                                </td>
                              </tr>
                              {/* Task Rows (normal text, only if expanded) */}
                              {expandedGroups[group.project_number] &&
                                group.tasks.map((task, idx) => {
                                  const taid = task.task_assignment_id;
                                  const rawHours =
                                    entries?.[taid]?.[activeIso]?.hours;
                                  const hoursVal =
                                    rawHours === 0 ? "" : rawHours;
                                  const commentVal =
                                    entries?.[taid]?.[activeIso]?.comment ?? "";
                                  const isSaved = saved?.[taid]?.[activeIso];
                                  const isDirty = dirty?.[taid]?.[activeIso];
                                  const errorMsg = errors?.[taid]?.[activeIso];
                                  return (
                                    <tr key={taid}>
                                      <td
                                        style={{
                                          width: "40%",
                                          paddingLeft: "2rem",
                                        }}
                                      >
                                        <span style={{ fontWeight: "500" }}>
                                          {task?.budget_name}
                                        </span>
                                        <br />
                                        <span>{task?.task_name}</span>
                                      </td>
                                      {/* Hours for ACTIVE DAY (Enter to save) */}
                                      <td style={{ width: "20%" }}>
                                        <Form.Control
                                          type="number"
                                          min="0"
                                          step="0.25"
                                          placeholder="00:00"
                                          className="time-input no-spinners"
                                          value={hoursVal}
                                          readOnly={isApproved}
                                          onKeyDown={(e) =>
                                            handleHoursKeyDown(
                                              e,
                                              taid,
                                              activeIso,
                                            )
                                          }
                                          onChange={(e) =>
                                            handleChange(
                                              taid,
                                              activeIso,
                                              "hours",
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {/* Warning under HOURS when there are unsaved changes */}
                                        {!isSaved && isDirty && !errorMsg && (
                                          <div
                                            className="field-warning"
                                            style={{ color: "white" }}
                                            role="status"
                                            aria-live="polite"
                                          >
                                            -
                                          </div>
                                        )}
                                      </td>
                                      {/* Comment for ACTIVE DAY + saved/unsaved indicator */}
                                      <td style={{ width: "40%" }}>
                                        <div className="d-flex align-items-start flex-column">
                                          <div className="w-100 d-flex align-items-center">
                                            <Form.Control
                                              type="text"
                                              placeholder="Comment"
                                              className={`comment-input ${errorMsg ? "is-invalid" : ""}`}
                                              value={commentVal}
                                              readOnly={isApproved}
                                              onKeyDown={(e) =>
                                                handleCommentKeyDown(
                                                  e,
                                                  taid,
                                                  activeIso,
                                                )
                                              }
                                              onChange={(e) =>
                                                handleChange(
                                                  taid,
                                                  activeIso,
                                                  "comment",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                          {/* Inline error under the comment field OR a warning if dirty */}
                                          {errorMsg ? (
                                            <div
                                              className="field-error"
                                              role="alert"
                                            >
                                              {errorMsg}
                                            </div>
                                          ) : !isSaved && isDirty ? (
                                            <div
                                              className="field-error"
                                              role="status"
                                              aria-live="polite"
                                            >
                                              Press Enter to save
                                            </div>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              {/* Divider between groups */}
                              {groupIdx < groupedTasks.length - 1 && (
                                <tr>
                                  <td
                                    colSpan="3"
                                    style={{
                                      borderBottom: "2px solid #e0e0e0",
                                      padding: 0,
                                    }}
                                  />
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No tasks match your filter
                            </td>
                          </tr>
                        );
                      })()
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals row - fixed at bottom of card */}
              <div
                className="totals-row d-flex justify-content-between p-3 pt-2"
                style={{ flex: "0 0 auto" }}
              >
                <div className="fw-semibold">Total Per Day</div>
                <div className="d-flex gap-4 flex-wrap">
                  {days.map((d, idx) => {
                    // Check if this day is a holiday or leave
                    const dayInfo = getDayInfo(d.iso);
                    let total = dayTotals[idx];
                    if (dayInfo?.isHoliday || dayInfo?.isLeave) {
                      total = 8;
                    }
                    return (
                      <span
                        key={d.key}
                        className={`fw-semibold ${
                          activeDay === d.key ? "text-primary" : "text-dark"
                        }`}
                        title={`${d.label} • ${d.date}`}
                      >
                        {total.toFixed(2)} hrs
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer className="border-0 px-4 timesheet-body timesheet-b mt-5 d-flex flex-column align-items-start gap-3">
        <div>
          <span className="des-timesheet-submit">
            * After adding timesheet entries for your working days this week,
            select Submit for Approval
          </span>
        </div>

        <div className="d-flex">
          <Button
            className="submit-btn me-2"
            disabled={
              isLoading ||
              isAlreadySubmitted ||
              submitDisabled ||
              weekProp?.status ||
              isApproved
            }
            onClick={handleSubmit}
          >
            {isApproved ? "Submitted" : "Submit for Approval"}
          </Button>

          <Button
            className="cancel-btn btn-secondary"
            onClick={() => setShowAddTime(false)}
          >
            Cancel
          </Button>
        </div>

        <div className="fw-semibold">
          Week Total: {weekTotal.toFixed(2)} hrs
        </div>
      </Modal.Footer>
    </Modal>
  );
}
